import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Search } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./components/ui/dialog";

// Admin: pick one member of the latest membership year and resend their card to their saved email.
export const ResendRenewedCardsButton = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ year: null, members: [] });
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get("admin/membership-cards/members", { params: { search } })
        .then(({ data }) => setResult(data))
        .catch((e) => toast.error(e.response?.data?.detail || "Could not load members"))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [open, search]);

  const openDialog = () => { setSearch(""); setPicked(null); setOpen(true); };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`admin/membership-cards/${picked.id}/resend`);
      toast.success(`The ${data.year} membership card was sent to ${data.email}.`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the card");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={openDialog} className="text-xs" data-testid="resend-renewed-cards-btn">
        <Mail className="h-3 w-3 mr-1" />
        Resend card
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="resend-card-dialog">
          <DialogHeader>
            <DialogTitle>Resend membership card</DialogTitle>
            <DialogDescription>
              Pick a {result.year || ""} member. The card is sent to the email saved in their account — fix the email in their profile first if it's wrong.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
              placeholder="Search by name or email"
              className="pl-8"
              data-testid="resend-card-search"
            />
          </div>
          <div className="max-h-64 overflow-y-auto border rounded-md divide-y" data-testid="resend-card-results">
            {loading && <p className="p-3 text-sm text-slate-500">Searching…</p>}
            {!loading && result.members.length === 0 && (
              <p className="p-3 text-sm text-slate-500">No {result.year || ""} member found.</p>
            )}
            {!loading && result.members.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setPicked(m)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${picked?.id === m.id ? "bg-blue-50" : "hover:bg-slate-50"}`}
                data-testid={`resend-card-member-${m.id}`}
              >
                <span className="font-medium text-slate-800">{m.first_name_1} {m.last_name}</span>
                <span className="block text-xs text-slate-500">{m.email}</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="resend-card-cancel">Cancel</Button>
            <Button onClick={send} disabled={!picked || sending} data-testid="resend-card-confirm">
              {sending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {picked ? `Send card to ${picked.first_name_1}` : "Pick a member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
